// Validation rules and messages
export const validationRules = {
  "user[login]": {
    required: true,
    minlength: 3,
    maxlength: 30,
    messages: {
      required: () => `${I18n.t('user_name_required')}.`,
      minlength: () => `${I18n.t('username_length_validation')}.`,
      maxlength: () => `${I18n.t('username_max_length')}.`,
    },
  },
  "user[email]": {
    required: true,
    email: true,
    messages: {
      required: () => `${I18n.t('email_required')}.`,
      email: () => `${I18n.t('email_invalid')}.`,
    },
  },
  "user[password]": {
    required: true,
    minlength: 8,
    messages: {
      required: () => `${I18n.t('password_required')}.`,
      minlength: () => `${I18n.t('password_length')}.`,
    },
  },
  "user[password_confirmation]": {
    required: true,
    equalTo: "user[password]",
    messages: {
      required: () => `${I18n.t('password_confirmation')}.`,
      equalTo: () => `${I18n.t('password_not_match')}`,
    },
  },
};

// Email validation helper
function validateEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

// Validate a single field
export function validateField(input, rules) {
  const fieldName = input.name;
  const fieldRules = rules[fieldName];
  if (!fieldRules) return true;

  const value = input.value.trim();
  let errorElement = input.nextElementSibling;

  if (!errorElement || !errorElement.classList.contains("error_star")) {
    errorElement = document.createElement("span");
    errorElement.className = "error_star";
    input.parentNode.insertBefore(errorElement, input.nextSibling);
  }

  // Clear previous visual error states
  input.classList.remove("error_class");
  errorElement.textContent = "";
  errorElement.style.display = "none";

  let isValid = true;

  // === Required Check First ===
  if (fieldRules.required && !value) {
    showError(input, errorElement, fieldRules.messages.required);
    return false;
  }

  // === Email Format Check ===
  if (fieldName === "user[email]" && fieldRules.email && !validateEmail(value)) {
    showError(input, errorElement, fieldRules.messages.email);
    isValid = false;
  }

  // === Min Length ===
  if (fieldRules.minlength && value.length < fieldRules.minlength) {
    showError(input, errorElement, fieldRules.messages.minlength);
    isValid = false;
  }

  // === Max Length ===
  if (fieldRules.maxlength && value.length > fieldRules.maxlength) {
    showError(input, errorElement, fieldRules.messages.maxlength);
    isValid = false;
  }

  // === Password Confirmation (equalTo) ===
  if (fieldName === "user[password_confirmation]" && fieldRules.equalTo) {
    const form = input.closest("form");
    // 👇 Use ID-based lookup now
    const passwordField = form.querySelector(`#${fieldRules.equalTo}`);
  
    if (passwordField) {
      const passwordValue = passwordField.value.trim();
      if (value !== passwordValue) {
        showError(input, errorElement, fieldRules.messages.equalTo);
        isValid = false;
      }
    }
  }  

  // === API error tracking (optional override) ===
  if (isValid && input.dataset.apiError === "true") {
    input.classList.add("error_class");
    errorElement.style.display = "block";
    if (!errorElement.textContent) {
      errorElement.textContent = `${I18n.t('validation_api_error')}`;
    }
    return false;
  }

  // Clear final state if valid
  if (isValid) {
    input.classList.remove("error_class");
    errorElement.textContent = "";
    errorElement.style.display = "none";
    input.dataset.apiError = "false";
  }

  return isValid;
}

// Validate the entire form
export function validateForm(form, rules) {
  let isValid = true;
  form.querySelectorAll("input").forEach((input) => {
    if (!validateField(input, rules)) {
      isValid = false;
    }
  });
  return isValid;
}

// Show error message
function showError(input, errorElement, message) {
  const finalMessage = typeof message === "function" ? message() : message;
  input.classList.add("error_class");
  errorElement.textContent = finalMessage;
  errorElement.style.display = "block";
}

// Initialize form validation
export function initializeFormValidation(form, rules) {
  form.addEventListener("submit", (event) => {
    if (!validateForm(form, rules)) {
      event.preventDefault();
    }
  });

  // Keyup & blur validation
  form.querySelectorAll("input").forEach((input) => {
    input.addEventListener("keyup", () => {
      // Clear API error if user is typing
      if (input.dataset.apiError === "true") {
        input.dataset.apiError = "false";
        const errorElement = input.nextElementSibling;
        if (errorElement && errorElement.classList.contains("error_star")) {
          errorElement.textContent = "";
          errorElement.style.display = "none";
        }
      }

      validateField(input, rules);
    });

    input.addEventListener("blur", () => {
      validateField(input, rules);
    });
  });
}
